import { useSyncExternalStore } from 'react';
import { foundation } from '../tokens';
import { applyBrandNoTranslate } from '../../utils/branding/brandNoTranslate';
import { SPLASH_FONT, SPLASH_FADE_MS } from './splashTokens';
import { SPLASH_PHASE, getSplashPhase } from './splashHandoff';

/**
 * The CrewVex logo's ONE journey (KAN-562 follow-up, founder 2026-10-02:
 * "login → splash → welcome must feel like ONE component in ONE motion").
 *
 * ONE fixed element carries the wordmark the whole way:
 *   leg 1 — sign-in card / MFA-gate wordmark → the splash's centre;
 *   rest  — it sits there, plain, while the splash covers the app (and
 *           while a welcome decision is pending — the splash hold);
 *   leg 2 — splash centre → the welcome's wordmark (role welcome header /
 *           owner guide), then swaps for the real wordmark on the frame it
 *           lands; with nothing to land on, it fades out WITH the splash.
 * A cold start (no sign-in card) starts the element at the splash wordmark,
 * pixel-for-pixel, on the frame the splash starts to leave.
 *
 * It lives on <body>, outside React, so Login unmounting and the session tree
 * remounting at sign-in (useSessionTreeKey) never touch it. While it exists
 * SplashOverlay hides its own wordmark (`useLogoJourneyActive`) — there is
 * always exactly one logo on screen.
 *
 * Motion is compositor-only: Web Animations on transform / opacity, no
 * per-frame JS, no layout reads while it moves (every rect is measured before
 * a leg starts). Plain CrewVex (RD-SEC-021d / KAN-657): no Vex shine. Only
 * this small element carries `will-change`.
 *
 * Never hangs: a leg ends on its animation's `finished` OR a timeout backstop;
 * the element is removed if the splash never comes up after leg 1, and
 * unconditionally after LOGO_JOURNEY_MAX_MS. Re-cover / sign-out cancel it.
 */

export const LOGO_JOURNEY_ATTR = 'data-cvx-logo-journey';
/**
 * One easing for every leg. Each leg starts FROM REST (the sign-in card, the
 * splash centre), so the curve starts at zero velocity and decelerates into
 * place: the element sets off, never gets shot off (the old glide family,
 * cubic-bezier(0.2, 0.8, 0.2, 1), left rest at 4x its average speed — a
 * 45–94px first-frame jump on the measured runs). Softened slightly for the
 * MFA silk pass (RD-SEC-021b): 0.33 starts a hair gentler than Material 0.4
 * without changing the decelerating landing.
 */
export const LOGO_JOURNEY_EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';
/** Default leg length — leg 1 and leg 2 match. 600 (was 480, KAN-758): the
 *  sign-in legs now change SIZE as well as place (login 40/44px ↔ splash
 *  28–32px), and the founder asked for that grow/shrink to feel smoother. */
export const LOGO_JOURNEY_LEG_MS = 600;
/** @deprecated RD-SEC-021d — shine removed; kept so older imports don't break. */
export const LOGO_JOURNEY_SHINE_MS = 240;
/** Leg 1 landed but no splash came up within this → fade away (sign-in aborted elsewhere). */
export const LOGO_JOURNEY_NO_COVER_MS = 2500;
/** Whatever happens, the element is gone this long after it was created. */
export const LOGO_JOURNEY_MAX_MS = 20000;
const BACKSTOP_EXTRA_MS = 120;
const Z = 100001; // just above SplashOverlay (100000)

let j = null;
const listeners = new Set();

function emit() {
  listeners.forEach((l) => { try { l(); } catch (_e) { /* never break the others */ } });
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isLogoJourneyActive() {
  return j != null;
}

/** SplashOverlay: hide the overlay's own wordmark while the journey element carries the logo. */
export function useLogoJourneyActive() {
  return useSyncExternalStore(subscribe, isLogoJourneyActive, () => false);
}

function reducedMotion() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (_e) {
    return false;
  }
}

function canAnimate() {
  return typeof document !== 'undefined' && typeof document.createElement('div').animate === 'function';
}

function build(px) {
  const fc = foundation.color;
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.setAttribute(LOGO_JOURNEY_ATTR, '');
  el.setAttribute('data-testid', 'logo-journey');
  // RD-I18N-007: same Chrome Translate opt-out as BrandLogo — this node lives
  // outside React and is the mark users watch during login → splash → welcome.
  applyBrandNoTranslate(el);
  Object.assign(el.style, {
    position: 'fixed', left: '0px', top: '0px', zIndex: String(Z), pointerEvents: 'none',
    transformOrigin: '0 0', willChange: 'transform, opacity', whiteSpace: 'nowrap', display: 'inline-flex',
    fontFamily: SPLASH_FONT, fontWeight: '700', fontSize: `${px}px`, letterSpacing: '-0.03em', lineHeight: '1.1',
    WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale',
  });
  const crew = document.createElement('span');
  crew.textContent = 'Crew';
  crew.style.color = fc.neutral.text;
  const vex = document.createElement('span');
  vex.textContent = 'Vex';
  vex.style.color = fc.brand.text;
  el.append(crew, vex);
  return { el };
}

const tf = ({ x, y, s }) => `translate(${x}px, ${y}px) scale(${s})`;

/** Where the element must sit (origin top-left) to cover `rect` exactly. */
export function fitJourneyBox(rect, w, h) {
  const s = rect.width / w;
  return { x: rect.left, y: rect.top + (rect.height - h * s) / 2, s };
}

function parseMatrix(value) {
  const m = /matrix\(([^)]+)\)/.exec(value || '');
  if (!m) return null;
  const p = m[1].split(',').map(Number);
  return p.length === 6 && p.every(Number.isFinite) ? { x: p[4], y: p[5], s: p[0] } : null;
}

/** Live position — mid-leg it is read from the running animation (a style read, never layout). */
function currentPos() {
  if (!j) return null;
  if (j.moving) return parseMatrix(window.getComputedStyle(j.el).transform) || j.pos;
  return j.pos;
}

function clearTimers() {
  if (!j) return;
  j.timers.forEach(clearTimeout);
  j.timers = [];
}

function later(fn, ms) {
  const g = j;
  const t = setTimeout(() => { if (j === g) fn(); }, ms);
  g.timers.push(t);
}

/** Once: the animation's `finished` OR the timeout backstop — never an animation event alone. */
function onLegEnd(anim, ms, fn) {
  const g = j;
  const leg = g.leg;
  let done = false;
  // A newer leg (or a fade) supersedes this one: its end never fires.
  const end = () => { if (done || j !== g || g.leg !== leg) return; done = true; fn(); };
  anim?.finished?.then(end).catch(() => {});
  later(end, ms + BACKSTOP_EXTRA_MS);
}

function animateTransform(to, durationMs, easing = LOGO_JOURNEY_EASE) {
  const from = currentPos();
  j.leg += 1;
  j.anim?.cancel();
  j.pos = to;
  j.el.style.transform = tf(to); // the resting state is the base style: no fill to leak
  if (!durationMs || !from) { j.moving = false; return null; }
  j.moving = true;
  const anim = j.el.animate([{ transform: tf(from) }, { transform: tf(to) }], { duration: durationMs, easing });
  j.anim = anim;
  return anim;
}

/** Computed font size of a wordmark (or its first child), in px. */
function fontPx(el, fallback = 28) {
  if (!el) return fallback;
  const node = el.firstElementChild || el;
  return parseFloat(window.getComputedStyle(node).fontSize) || parseFloat(window.getComputedStyle(el).fontSize) || fallback;
}

/**
 * Build the element at the LARGEST size the journey shows (KAN-758): text
 * scaled DOWN stays crisp, text scaled UP is a stretched bitmap — it looked
 * soft in flight and "popped" sharp on landing when a small splash wordmark
 * grew into the bigger sign-in wordmark.
 */
function create(px) {
  const { el } = build(px);
  document.body.appendChild(el);
  // Unscaled box, measured once at creation (no transform yet) — never during motion.
  const box = el.getBoundingClientRect();
  const g = {
    el, w: box.width || el.offsetWidth || 1, h: box.height || el.offsetHeight || 1, pos: null, anim: null,
    moving: false, leg: 0, timers: [], stage: 'rest', restEl: null, onResize: null,
  };
  j = g;
  later(() => endLogoJourneyQuietly(), LOGO_JOURNEY_MAX_MS);
  return g;
}

function remove() {
  const g = j;
  if (!g) return;
  j = null;
  g.timers.forEach(clearTimeout);
  stopTracking(g);
  try { g.anim?.cancel(); } catch (_e) { /* gone */ }
  if (g.onResize) window.removeEventListener('resize', g.onResize);
  if (g.el.parentNode) g.el.parentNode.removeChild(g.el);
  emit();
}

/** Remove now (re-cover, sign-out, an aborted sign-in). Idempotent. */
export function cancelLogoJourney() {
  remove();
}

/**
 * The splash came back while the element was LEAVING it (leg 2 / fade): drop
 * it — the overlay's own wordmark is back under the cover. A journey still on
 * its way IN (leg 1) or resting on the splash is untouched: the splash coming
 * up is exactly what it is waiting for.
 */
export function cancelLogoJourneyLeave() {
  if (j && (j.stage === 'leg2' || j.stage === 'fade')) remove();
}

/**
 * End without a target: under a covering splash the overlay's own wordmark
 * takes over on the same frame; otherwise the element fades away.
 */
export function endLogoJourneyQuietly() {
  if (!j) return;
  if (getSplashPhase() === SPLASH_PHASE.COVERED) { remove(); return; }
  fadeLogoJourney();
}

function measure(el) {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

function followRestOnResize(restEl) {
  j.restEl = restEl;
  j.onResize = () => {
    if (!j || j.stage !== 'rest' || !j.restEl?.isConnected) return;
    const r = measure(j.restEl);
    if (r.width > 0) animateTransform(fitJourneyBox(r, j.w, j.h), 0);
  };
  window.addEventListener('resize', j.onResize);
}

/**
 * Leg 1 — start on a source wordmark (`anchorEl`) and glide onto the splash
 * wordmark (`restEl`, always mounted). Used by Login morph and MFA success
 * exit (RD-SEC-021d). Reduced motion: no glide — it appears at the rest
 * position. Returns true when a journey started.
 */
export function beginLogoJourney(anchorEl, restEl, { reduced = false } = {}) {
  cancelLogoJourney();
  if (!anchorEl || !restEl || !canAnimate()) return false;
  const from = measure(anchorEl.firstElementChild || anchorEl);
  const rest = measure(restEl);
  if (!from.width || !rest.width) return false;
  const g = create(Math.max(fontPx(anchorEl), fontPx(restEl)));
  g.stage = 'leg1';
  followRestOnResize(restEl);
  const restPos = fitJourneyBox(rest, g.w, g.h);
  if (reduced || reducedMotion()) {
    animateTransform(restPos, 0);
    g.stage = 'rest';
  } else {
    g.pos = fitJourneyBox(from, g.w, g.h);
    g.el.style.transform = tf(g.pos);
    const anim = animateTransform(restPos, LOGO_JOURNEY_LEG_MS);
    onLegEnd(anim, LOGO_JOURNEY_LEG_MS, () => { g.moving = false; g.stage = 'rest'; });
  }
  later(() => {
    if (getSplashPhase() !== SPLASH_PHASE.COVERED && g.stage === 'rest') fadeLogoJourney();
  }, LOGO_JOURNEY_LEG_MS + LOGO_JOURNEY_NO_COVER_MS);
  emit();
  return true;
}

/** Login aborted its morph (no splash came up): drop the element so the card's own wordmark shows again. */
export function abortLogoJourneyIfUncovered() {
  if (j && getSplashPhase() !== SPLASH_PHASE.COVERED) remove();
}

/**
 * Leg 2 — fly from wherever the journey is (or, on a cold start, from the
 * splash wordmark `sourceEl`, pixel-for-pixel) onto `targetEl`, whose resting
 * layout rect is `targetRect`. The target is snapped to whole pixels (a
 * sub-pixel `translate`) and the element lands on exactly that box; on the
 * landing frame the target is revealed and the element removed, together.
 * Returns the leg length, or 0 when no glide ran (caller reveals the target).
 */
export function flyLogoJourney(sourceEl, targetEl, targetRect, {
  durationMs = LOGO_JOURNEY_LEG_MS, onLand, measureTarget,
} = {}) {
  if (!targetEl || !targetRect?.width || !canAnimate()) return 0;
  if (!j) {
    if (!sourceEl) return 0;
    const src = measure(sourceEl);
    if (!src.width) return 0;
    const g = create(Math.max(fontPx(sourceEl), fontPx(targetEl)));
    animateTransform(fitJourneyBox(src, g.w, g.h), 0);
    emit();
  }
  const g = j;
  g.stage = 'leg2';
  clearTimers();
  later(() => endLogoJourneyQuietly(), LOGO_JOURNEY_MAX_MS);
  const toEl = targetEl.firstElementChild || targetEl;
  const land = () => {
    // ONE task: the real wordmark appears and the element leaves — never two, never none.
    stopTracking(g);
    targetEl.style.visibility = 'visible';
    onLand?.();
    remove();
  };
  const aim = (rect, ms, easing) => {
    // Snap the target to whole pixels; the element lands on that snapped box.
    const fx = rect.left - Math.round(rect.left);
    const fy = rect.top - Math.round(rect.top);
    targetEl.style.translate = fx || fy ? `${-fx}px ${-fy}px` : '';
    const snapped = { ...rect, left: Math.round(rect.left), top: Math.round(rect.top) };
    g.flight = { rect, snapped, start: performance.now(), dur: ms };
    const anim = animateTransform(fitJourneyBox(snapped, g.w, g.h), ms, easing);
    onLegEnd(anim, ms, land);
  };
  aim(targetRect, durationMs);
  const read = measureTarget || (() => measure(toEl));
  trackTarget(g, toEl, read, aim);
  return durationMs;
}

/**
 * Safety net for a target that moves mid-flight (the centered welcome panel
 * re-centering as the touch band settles on a real iPhone): each frame of
 * leg 2 — and only then, on that one element — read where the target now
 * rests (the same measure the aim used); if it moved, re-aim from the element's CURRENT position for the
 * remaining time, on a curve that starts at speed (no stop-and-restart).
 */
const RETARGET_EASE = 'cubic-bezier(0.25, 0.55, 0.3, 1)';
const RETARGET_EPS_PX = 0.5;
const RETARGET_MIN_MS = 120;

function stopTracking(g) {
  if (g?.trackRaf != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(g.trackRaf);
  if (g) g.trackRaf = null;
}

function trackTarget(g, toEl, read, aim) {
  if (typeof requestAnimationFrame !== 'function') return;
  const tick = () => {
    g.trackRaf = null;
    if (j !== g || g.stage !== 'leg2' || !toEl.isConnected) return;
    const p = read();
    // Undo our own whole-pixel snap to compare like for like.
    const f = g.flight;
    const rect = { left: p.left + (f.rect.left - f.snapped.left), top: p.top + (f.rect.top - f.snapped.top), width: p.width, height: p.height };
    const moved = Math.abs(rect.left - f.rect.left) > RETARGET_EPS_PX || Math.abs(rect.top - f.rect.top) > RETARGET_EPS_PX
      || Math.abs(rect.width - f.rect.width) > RETARGET_EPS_PX;
    if (moved && rect.width > 0) {
      const left = f.dur - (performance.now() - f.start);
      aim(rect, Math.max(RETARGET_MIN_MS, left), RETARGET_EASE);
    }
    g.trackRaf = requestAnimationFrame(tick);
  };
  g.trackRaf = requestAnimationFrame(tick);
}

/** No target: fade out together with the splash (same length + curve as SplashOverlay's fade). */
export function fadeLogoJourney(durationMs = SPLASH_FADE_MS) {
  if (!j) return;
  const g = j;
  g.stage = 'fade';
  g.leg += 1;
  clearTimers();
  g.el.style.opacity = '0';
  const anim = g.el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: durationMs, easing: 'ease-out' });
  onLegEnd(anim, durationMs, remove);
}

/** @internal tests only */
export function __getLogoJourneyForTests() {
  return j;
}
